/**
 * ActionTypeEditorForm - Full-screen editor form for action type definitions.
 * Used in the EditorDrawer for creating/editing action types in the library.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { EditorShell } from "@/components/layout/EditorShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";

type ActionType = "comment" | "input" | "automation" | "link";

interface FormState {
	slug: string;
	name: string;
	description: string;
	type: ActionType;
	completionType: string;
	configSchema: string;
	defaultConfig: string;
	order: number;
}

const initialFormState: FormState = {
	slug: "",
	name: "",
	description: "",
	type: "comment",
	completionType: "user_confirm",
	configSchema: "",
	defaultConfig: "",
	order: 0,
};

interface ActionTypeEditorFormProps {
	/** Action type ID to edit, or null for create mode */
	actionId: string | null;
}

/**
 * Full-screen editor form for action types.
 * Manages its own form state internally for performance.
 * Closes by clearing URL search params.
 * @param props - Component props
 * @param props.actionId - Action type ID to edit, or null for create mode
 */
export function ActionTypeEditorForm({ actionId }: ActionTypeEditorFormProps) {
	const [, setSearchParams] = useSearchParams();
	const [formState, setFormState] = useState<FormState>(initialFormState);
	const [jsonError, setJsonError] = useState<string | null>(null);

	const utils = trpc.useUtils();
	const isCreateMode = actionId === null;

	// Fetch action type data for edit mode
	const actionTypeQuery = trpc.actions.getActionType.useQuery(
		{ id: actionId ?? "" },
		{ enabled: !isCreateMode && !!actionId }
	);

	// Create mutation
	const createMutation = trpc.actions.createActionType.useMutation({
		onSuccess: () => {
			void utils.actions.listAllActionTypes.invalidate();
			void utils.actions.listActionTypes.invalidate();
			closeDrawer();
		},
	});

	// Update mutation
	const updateMutation = trpc.actions.updateActionType.useMutation({
		onSuccess: () => {
			void utils.actions.listAllActionTypes.invalidate();
			void utils.actions.listActionTypes.invalidate();
			closeDrawer();
		},
	});

	// Initialize form when data loads (edit mode)
	useEffect(() => {
		if (!isCreateMode && actionTypeQuery.data) {
			const a = actionTypeQuery.data;
			setFormState({
				slug: a.slug,
				name: a.name,
				description: a.description ?? "",
				type: a.type as ActionType,
				completionType: a.completionType,
				configSchema: a.configSchema ? JSON.stringify(a.configSchema, null, 2) : "",
				defaultConfig: a.defaultConfig ? JSON.stringify(a.defaultConfig, null, 2) : "",
				order: a.order,
			});
		}
	}, [actionTypeQuery.data, isCreateMode]);

	// Reset form when switching to create mode
	useEffect(() => {
		if (isCreateMode) {
			setFormState(initialFormState);
			setJsonError(null);
		}
	}, [isCreateMode]);

	/**
	 * Closes the drawer by clearing URL params
	 */
	const closeDrawer = () => {
		setSearchParams({});
	};

	/**
	 * Updates a form field
	 * @param field - The form field key to update
	 * @param value - The new value for the field
	 */
	const updateFormField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
		setFormState((prev) => ({ ...prev, [field]: value }));
		if (field === "configSchema" || field === "defaultConfig") {
			setJsonError(null);
		}
	};

	/**
	 * Parses JSON string safely
	 * @param jsonString - The JSON string to parse
	 */
	const parseJsonSafe = (jsonString: string): Record<string, unknown> | null => {
		if (!jsonString.trim()) return null;
		try {
			return JSON.parse(jsonString) as Record<string, unknown>;
		}
		catch {
			return undefined as unknown as null;
		}
	};

	/**
	 * Validates JSON fields before submit
	 */
	const validateJsonFields = (): boolean => {
		if (formState.configSchema.trim()) {
			try {
				JSON.parse(formState.configSchema);
			}
			catch {
				setJsonError("Invalid JSON in Config Schema");
				return false;
			}
		}
		if (formState.defaultConfig.trim()) {
			try {
				JSON.parse(formState.defaultConfig);
			}
			catch {
				setJsonError("Invalid JSON in Default Config");
				return false;
			}
		}
		setJsonError(null);
		return true;
	};

	/**
	 * Handles form submission
	 */
	const handleSubmit = () => {
		if (!validateJsonFields()) return;

		if (isCreateMode) {
			createMutation.mutate({
				slug: formState.slug,
				name: formState.name,
				description: formState.description || null,
				type: formState.type,
				completionType: formState.completionType,
				configSchema: parseJsonSafe(formState.configSchema),
				defaultConfig: parseJsonSafe(formState.defaultConfig),
				order: formState.order,
			});
		}
		else if (actionId) {
			updateMutation.mutate({
				id: actionId,
				name: formState.name,
				description: formState.description || null,
				type: formState.type,
				completionType: formState.completionType,
				configSchema: parseJsonSafe(formState.configSchema),
				defaultConfig: parseJsonSafe(formState.defaultConfig),
				order: formState.order,
			});
		}
	};

	const isPending = createMutation.isPending || updateMutation.isPending;
	const mutationError = createMutation.error ?? updateMutation.error;

	// Loading state for edit mode
	if (!isCreateMode && actionTypeQuery.isLoading) {
		return (
			<EditorShell
				title="Edit Action"
				onBack={closeDrawer}
			>
				<div className="flex items-center justify-center py-12">
					<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
				</div>
			</EditorShell>
		);
	}

	// Error state for edit mode
	if (!isCreateMode && actionTypeQuery.error) {
		return (
			<EditorShell
				title="Edit Action"
				onBack={closeDrawer}
			>
				<div className="text-center py-12">
					<p className="text-red-400">Error loading action type</p>
					<p className="text-sm text-muted-foreground mt-2">
						{actionTypeQuery.error.message}
					</p>
					<Button className="mt-4" onClick={closeDrawer}>
						Go Back
					</Button>
				</div>
			</EditorShell>
		);
	}

	return (
		<EditorShell
			title={isCreateMode ? "Create Action" : "Edit Action"}
			description={isCreateMode
				? "Define a new action for use in module workflows."
				: "Update action details. Slug cannot be changed."}
			onBack={closeDrawer}
			footerActions={
				<>
					<Button variant="outline" type="button" onClick={closeDrawer}>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={isCreateMode
							? (!formState.slug || !formState.name || isPending)
							: (!formState.name || isPending)}
					>
						{isPending
							? (isCreateMode ? "Creating..." : "Saving...")
							: (isCreateMode ? "Create Action" : "Save Changes")}
					</Button>
				</>
			}
		>
			<div className="space-y-6">
				<div className="grid grid-cols-2 gap-4">
					<div className="grid gap-2">
						<Label htmlFor="slug">Slug</Label>
						<Input
							id="slug"
							placeholder="my-action-type"
							value={formState.slug}
							onChange={(e) => { updateFormField("slug", e.target.value); }}
							disabled={!isCreateMode}
						/>
						{isCreateMode && (
							<p className="text-xs text-muted-foreground">
								Lowercase letters, numbers, and hyphens only
							</p>
						)}
					</div>
					<div className="grid gap-2">
						<Label htmlFor="name">Name</Label>
						<Input
							id="name"
							placeholder="My Action"
							value={formState.name}
							onChange={(e) => { updateFormField("name", e.target.value); }}
						/>
					</div>
				</div>
				<div className="grid gap-2">
					<Label htmlFor="description">Description</Label>
					<Input
						id="description"
						placeholder="What this action does..."
						value={formState.description}
						onChange={(e) => { updateFormField("description", e.target.value); }}
					/>
				</div>
				<div className="grid grid-cols-3 gap-4">
					<div className="grid gap-2">
						<Label htmlFor="type">Type</Label>
						<Select
							value={formState.type}
							onValueChange={(value) => { updateFormField("type", value as ActionType); }}
						>
							<SelectTrigger id="type">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="comment">Comment</SelectItem>
								<SelectItem value="input">Input</SelectItem>
								<SelectItem value="automation">Automation</SelectItem>
								<SelectItem value="link">Link</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="completionType">Completion Type</Label>
						<Input
							id="completionType"
							placeholder="user_confirm"
							value={formState.completionType}
							onChange={(e) => { updateFormField("completionType", e.target.value); }}
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="order">Order</Label>
						<Input
							id="order"
							type="number"
							min={0}
							value={formState.order}
							onChange={(e) => { updateFormField("order", parseInt(e.target.value) || 0); }}
						/>
					</div>
				</div>
				<div className="grid gap-2">
					<Label htmlFor="configSchema">Config Schema (JSON)</Label>
					<Textarea
						id="configSchema"
						className="font-mono text-sm"
						placeholder='{"type": "object", "properties": {...}}'
						rows={4}
						value={formState.configSchema}
						onChange={(e) => { updateFormField("configSchema", e.target.value); }}
					/>
				</div>
				<div className="grid gap-2">
					<Label htmlFor="defaultConfig">Default Config (JSON)</Label>
					<Textarea
						id="defaultConfig"
						className="font-mono text-sm"
						placeholder='{"field": "value"}'
						rows={4}
						value={formState.defaultConfig}
						onChange={(e) => { updateFormField("defaultConfig", e.target.value); }}
					/>
				</div>
				{(jsonError !== null || mutationError !== null) && (
					<p className="text-sm text-red-400">
						{jsonError ?? mutationError?.message}
					</p>
				)}
			</div>
		</EditorShell>
	);
}
